
/**
 * 炎属性カードのトリガー実装
 *
 * このファイルには炎属性カードの個別トリガー効果を実装します。
 * 各カードは独自のトリガー定義を持ち、triggerEngineによって管理されます。
 */

import { TRIGGER_TYPES, ACTIVATION_TYPES } from '../triggerTypes';
import type { TriggerContext } from '../triggerEngine';
import type { CardWithId, MonsterInstance } from '../../types/card';
import type { TriggerDefinition } from '../../types/effect';
import {
  getPlayerContext,
  millDeck,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  destroyMonster,
  modifyAttack,
  applyStatusToOpponentMonster,
} from '../effectHelpers';
import { hasCategory } from '../../utils/helpers';
import { STATUS_EFFECT_TYPES } from '../statusEffects/statusTypes';
import { statusEffectEngine } from '../statusEffects';

/**
 * 炎属性カードのトリガー定義
 * カードID => トリガー配列のマッピング
 */
export const fireCardTriggers = {
  /**
   * C0000021: フレア・ドラゴン
   * 【自バトルフェイズ開始時】相手プレイヤーに300ダメージを与える。
   * 基本技：自身の攻撃力の半分のダメージを相手モンスター1体に与える（cardEffects/fire.jsで実装）
   */
  C0000021: [
    {
      type: TRIGGER_TYPES.ON_BATTLE_PHASE_START_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自バトルフェイズ開始時: 相手プレイヤーに300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('フレア・ドラゴンの効果: 相手プレイヤーに300ダメージ', 'damage');
        conditionalDamage(context as any, 300, 'opponent');
      },
    },
  ],

  /**
   * C0000023: レッドバーストドラゴン
   * 【自バトルフェイズ開始時】相手プレイヤーに300ダメージ（覚醒後500ダメージ）を与える。
   * 【覚醒時】攻撃力が1000アップ。（基本技発動で覚醒状態になる）
   */
  C0000023: [
    {
      type: TRIGGER_TYPES.ON_BATTLE_PHASE_START_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自バトルフェイズ開始時: 相手に300ダメージ（覚醒時500）',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        // トリガーを持つモンスターを探す（context.cardまたはslotIndexから）
        const monster = context.card || (context.slotIndex !== undefined ? myField[context.slotIndex] : null);

        if (!monster) {
          addLog('レッドバーストドラゴンの効果: モンスターが見つかりません', 'info');
          return;
        }

        // 覚醒状態チェック（状態異常システムを使用）
        const isAwakened = statusEffectEngine.hasStatus(monster, STATUS_EFFECT_TYPES.AWAKENED);
        const damage = isAwakened ? 500 : 300;

        addLog(`レッドバーストドラゴンの効果: 相手プレイヤーに${damage}ダメージ${isAwakened ? '（覚醒）' : ''}`, 'damage');
        conditionalDamage(context as any, damage, 'opponent');
      },
    },
  ],

  /**
   * C0000025: ブレイズ・ドラゴン
   * 【自壊時】デッキから［ドラゴン］モンスター1体を手札に加える。
   */
  C0000025: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: デッキから［ドラゴン］1体をサーチ',
      effect: (context: TriggerContext) => {
        const found = searchCard(context as any, (card) => {
          return card.type === 'monster' &&
                 hasCategory(card, '【ドラゴン】');
        });

        if (!found) {
          context.addLog('ブレイズ・ドラゴンの効果: デッキに［ドラゴン］モンスターが見つかりませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000026: インフェルノ・ドラゴン
   * 【自攻撃前】攻撃対象モンスターの攻撃力を300ダウン。
   * 【自攻撃後】相手モンスターを破壊した場合、相手プレイヤーに500ダメージを与える。
   */
  C0000026: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 攻撃対象の攻撃力を300ダウン',
      effect: (context: TriggerContext) => {
        const { targetIndex, addLog } = context;
        const { opponentField, setOpponentField } = getPlayerContext(context);

        if (targetIndex !== undefined && targetIndex !== null && opponentField[targetIndex]) {
          setOpponentField(prev => {
            const newField = [...prev];
            const target = newField[targetIndex];
            if (target) {
              const newAttack = Math.max(0, (target.currentAttack ?? target.attack ?? 0) - 300);
              addLog(`インフェルノ・ドラゴンの効果: ${target.name}の攻撃力を300ダウン！`, 'damage');
              newField[targetIndex] = { ...target, currentAttack: newAttack };
            }
            return newField;
          });
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_ATTACK_SUCCESS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃成功時: 相手モンスター破壊で500ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        // useCombat.ts から destroyedCard として渡される
        const destroyedCard = (context as any).destroyedCard;

        // 相手モンスターを破壊した場合
        if (destroyedCard) {
          addLog('インフェルノ・ドラゴンの効果: 相手モンスター破壊で500ダメージ！', 'damage');
          conditionalDamage(context as any, 500, 'opponent');
        }
      },
    },
  ],

  /**
   * C0000027: マグマ・ドラゴン
   * 【相手が魔法カードを発動時】相手プレイヤーに200ダメージを与える。
   */
  C0000027: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_MAGIC_ACTIVATED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手が魔法カード発動時: 200ダメージ',
      effect: (context: TriggerContext) => {
        context.addLog('マグマ・ドラゴンの効果発動: 相手に200ダメージ', 'info');
        conditionalDamage(context as any, 200, 'opponent');
      },
    },
  ],

  /**
   * C0000028: 炎竜母フレイマ
   * 【召喚時】墓地の［ドラゴン］モンスター1体を攻撃力半減で場に戻す。
   * 【常時】自分の［ドラゴン］モンスターは効果でダメージを受けない。
   * ※常時効果はcontinuousEffects/monsterCards.jsで処理
   */
  C0000028: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地の［ドラゴン］を攻撃力半減で復活',
      effect: (context: TriggerContext) => {
        const success = reviveFromGraveyard(context as any, (card) => {
          return card.type === 'monster' &&
                 hasCategory(card, '【ドラゴン】');
        }, true); // true = weakened (half attack)

        if (!success) {
          context.addLog('炎竜母フレイマの効果: 墓地に［ドラゴン］モンスターがいません', 'info');
        }
      },
    },
    // 【常時】効果はcontinuousEffects/monsterCards.jsのDAMAGE_IMMUNITYで処理
  ],

  // C0000029: クリムゾン・ワイバーン
  // 【常時】［ドラゴン］モンスターがいる時、このカードの攻撃力は400アップする。
  // ※常時効果はcontinuousEffects/monsterCards.jsのATK_MODIFIERで処理済み
  // トリガーは不要

  /**
   * C0000030: バースト・ドラゴン・ナイト
   * 【自攻撃後】このカードが［ドラゴン］モンスターを破壊した場合、もう一度攻撃が可能。
   */
  C0000030: [
    {
      type: TRIGGER_TYPES.ON_ATTACK_SUCCESS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃成功時: ドラゴン破壊で追加攻撃',
      effect: (context: TriggerContext) => {
        const { addLog, monsterIndex } = context;
        // useCombat.ts から destroyedCard として渡される
        const destroyedCard = (context as any).destroyedCard;
        const { myField, setMyField } = getPlayerContext(context);

        // 相手モンスターを破壊した場合
        if (destroyedCard) {
          // 破壊したモンスターが［ドラゴン］かチェック
          const isDragon = hasCategory(destroyedCard, '【ドラゴン】');

          if (isDragon) {
            addLog('バースト・ドラゴン・ナイトの効果: ドラゴンを破壊！もう一度攻撃可能！', 'info');

            // このモンスターの攻撃フラグを再び有効にする
            setMyField(prev => {
              const newField = [...prev];
              // monsterIndexがある場合はそれを使用、なければカード名で検索
              const targetIndex = monsterIndex !== undefined ? monsterIndex :
                newField.findIndex(m => m && m.id === 'C0000030');

              if (targetIndex !== -1 && newField[targetIndex]) {
                newField[targetIndex] = {
                  ...newField[targetIndex],
                  canAttack: true,
                };
              }
              return newField;
            });
          }
        }
      },
    },
  ],

  /**
   * C0000161: 灯魔龍ランプデビル
   * 【召喚時】場にいる全ての炎属性モンスターに3000ダメージを与え、破壊したモンスター×600のダメージを相手プレイヤーに与える。
   * 【自壊時】自分のSPトークンを3個破壊する。
   */
  C0000161: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 全炎属性に3000ダメージ、破壊数×600を相手に',
      effect: (context: TriggerContext) => {
        const { setP1Field, setP2Field, addLog } = context;
        let destroyedCount = 0;

        // 両プレイヤーのフィールドをチェック
        [1, 2].forEach((player) => {
          const setField = player === 1 ? setP1Field : setP2Field;

          setField!((prev) => {
            return prev.map((monster) => {
              if (monster && monster.attribute === '炎') {
                const newHp = monster.currentHp - 3000;
                if (newHp <= 0) {
                  destroyedCount++;
                  addLog(`${monster.name}が破壊された！`, 'damage');
                  return null;
                } else {
                  return { ...monster, currentHp: newHp };
                }
              }
              return monster;
            });
          });
        });

        // 破壊した数×600のダメージ
        if (destroyedCount > 0) {
          const totalDamage = destroyedCount * 600;
          addLog(`灯魔龍ランプデビルの効果: ${destroyedCount}体破壊、相手に${totalDamage}ダメージ`, 'damage');
          conditionalDamage(context as any, totalDamage, 'opponent');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 自分のSPトークンを3個破壊',
      effect: (context: TriggerContext) => {
        const { currentPlayer, p1ActiveSP, p2ActiveSP, p1RestedSP, p2RestedSP,
                setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP, addLog } = context;

        addLog('灯魔龍ランプデビルの自壊時効果: SPトークンを3個破壊', 'damage');

        if (currentPlayer === 1) {
          const totalSP = (p1ActiveSP ?? 0) + (p1RestedSP ?? 0);
          if (totalSP >= 3) {
            // まずレストSPから減らす
            const restedToRemove = Math.min(p1RestedSP ?? 0, 3);
            const activeToRemove = 3 - restedToRemove;
            setP1RestedSP!((prev) => Math.max(0, prev - restedToRemove));
            setP1ActiveSP!((prev) => Math.max(0, prev - activeToRemove));
          }
        } else {
          const totalSP = (p2ActiveSP ?? 0) + (p2RestedSP ?? 0);
          if (totalSP >= 3) {
            const restedToRemove = Math.min(p2RestedSP ?? 0, 3);
            const activeToRemove = 3 - restedToRemove;
            setP2RestedSP!((prev) => Math.max(0, prev - restedToRemove));
            setP2ActiveSP!((prev) => Math.max(0, prev - activeToRemove));
          }
        }
      },
    },
  ],

  /**
   * C0000164: 岩狸・岩太
   * 【自壊時】相手プレイヤーに200ダメージを与える。
   */
  C0000164: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 相手に200ダメージ',
      effect: (context: TriggerContext) => {
        context.addLog('岩狸・岩太の自壊時効果: 相手に200ダメージ', 'damage');
        conditionalDamage(context as any, 200, 'opponent');
      },
    },
  ],

  /**
   * C0000165: 岩狸・熔岩守
   * 【召喚時】相手モンスター1体の攻撃力をターン終了時まで500ダウン。
   * 【自壊時】デッキから炎属性フィールドカード1枚を手札に加える。
   */
  C0000165: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 相手モンスター1体の攻撃力-500',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField } = getPlayerContext(context);

        // 相手のフィールドにモンスターがいるかチェック
        const opponentMonsters = opponentField.filter((m) => m !== null);
        if (opponentMonsters.length === 0) {
          addLog('岩狸・熔岩守の効果: 相手フィールドにモンスターがいません', 'info');
          return;
        }

        // 最初のモンスターをターゲット（UIで選択させるべきだが、簡略化）
        const targetIndex = opponentField.findIndex((m) => m !== null);
        if (targetIndex !== -1) {
          modifyAttack(context as any, -500, targetIndex, true, false); // temporary
          addLog('岩狸・熔岩守の効果: 相手モンスターの攻撃力-500', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 炎属性フィールドカードをサーチ',
      effect: (context: TriggerContext) => {
        const found = searchCard(context as any, (card) => {
          return card.type === 'field' && card.attribute === '炎';
        });

        if (!found) {
          context.addLog('岩狸・熔岩守の効果: デッキに炎属性フィールドカードが見つかりませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000166: 岩狸・地割れ狸
   * 【自攻撃時】相手の場にいるモンスター1体の効果をターン終了時まで無効化。
   */
  C0000166: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 相手モンスター1体の効果無効化',
      effect: (context: TriggerContext) => {
        const { addLog, targetIndex } = context;
        const { opponentField } = getPlayerContext(context);

        // 攻撃対象が相手モンスターの場合、そのモンスターに効果無効を付与
        if (targetIndex !== undefined && targetIndex !== -1 && opponentField[targetIndex]) {
          const success = applyStatusToOpponentMonster(context as any,
            targetIndex,
            STATUS_EFFECT_TYPES.SILENCE,
            { duration: 1 }, // ターン終了時まで（1ターン）
            '岩狸・地割れ狸'
          );
          if (success) {
            addLog(`岩狸・地割れ狸の効果: ${opponentField[targetIndex].name}の効果をターン終了時まで無効化！`, 'info');
          }
        } else {
          // 攻撃対象がプレイヤーの場合、相手モンスター1体を選んで効果無効化
          const firstMonsterIndex = opponentField.findIndex(m => m !== null);
          if (firstMonsterIndex !== -1) {
            const success = applyStatusToOpponentMonster(context as any,
              firstMonsterIndex,
              STATUS_EFFECT_TYPES.SILENCE,
              { duration: 1 },
              '岩狸・地割れ狸'
            );
            if (success) {
              const targetMonsterName = opponentField[firstMonsterIndex]?.name ?? 'モンスター';
              addLog(`岩狸・地割れ狸の効果: ${targetMonsterName}の効果をターン終了時まで無効化！`, 'info');
            }
          } else {
            addLog('岩狸・地割れ狸の効果: 相手フィールドにモンスターがいません', 'info');
          }
        }
      },
    },
  ],

  /**
   * C0000167: 岩狸・剛石権蔵
   * 【召喚時】墓地の［マグマフォージ］モンスター1体を場に戻す（攻撃力半分）。
   */
  C0000167: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地の［マグマフォージ］を攻撃力半減で復活',
      effect: (context: TriggerContext) => {
        const success = reviveFromGraveyard(context as any, (card) => {
          return card.type === 'monster' &&
                 hasCategory(card, '【マグマフォージ】');
        }, true);

        if (!success) {
          context.addLog('岩狸・剛石権蔵の効果: 墓地に［マグマフォージ］モンスターがいません', 'info');
        }
      },
    },
  ],

  /**
   * C0000168: 熱き剣士セイニー
   * 【自攻撃時】相手モンスターに400ダメージを与える。自分の場にフィールドカードがある場合、さらに相手プレイヤーに300ダメージ。
   */
  C0000168: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 相手モンスターに400、フィールドありで相手に300',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myFieldCard } = getPlayerContext(context);

        // TODO: 攻撃対象のモンスターに400ダメージ（攻撃対象が必要）
        addLog('熱き剣士セイニーの効果: 相手モンスターに400ダメージ', 'damage');

        if (myFieldCard) {
          addLog('熱き剣士セイニーの追加効果: 相手プレイヤーに300ダメージ', 'damage');
          conditionalDamage(context as any, 300, 'opponent');
        }
      },
    },
  ],

  /**
   * C0000169: 岩狸・火山頭
   * 【召喚時】自分のSPを1減らすことで相手の場にいるモンスター1体に2000ダメージ。
   */
  C0000169: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: SP1消費で相手モンスター1体に2000ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myActiveSP, setMyActiveSP, opponentField } = getPlayerContext(context);

        if (myActiveSP < 1) {
          addLog('岩狸・火山頭の効果: SPが不足しています', 'damage');
          return;
        }

        // 相手のフィールドにモンスターがいるかチェック
        const opponentMonsters = opponentField.filter((m) => m !== null);
        if (opponentMonsters.length === 0) {
          addLog('岩狸・火山頭の効果: 相手フィールドにモンスターがいません', 'info');
          return;
        }

        // SP消費
        setMyActiveSP((prev) => prev - 1);

        // 最初のモンスターに2000ダメージ（UIで選択させるべきだが、簡略化）
        const targetIndex = opponentField.findIndex((m) => m !== null);
        if (targetIndex !== -1) {
          conditionalDamage(context as any, 2000, 'opponent_monster', targetIndex);
          addLog('岩狸・火山頭の効果: 相手モンスターに2000ダメージ', 'damage');
        }
      },
    },
  ],

  /**
   * C0000170: 岩狸・大岩王
   * 【常時】場にいる［マグマフォージ］モンスター1体につき攻撃力500アップ。
   */
  C0000170: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: ［マグマフォージ］1体につき攻撃力+500',
      effect: (context: TriggerContext) => {
        const { monsterIndex } = context;
        const { myField } = getPlayerContext(context);

        // 場に［マグマフォージ］が何体いるかカウント
        const magmaForgeCount = myField.filter((monster, idx) =>
          monster &&
          idx !== monsterIndex &&
          hasCategory(monster, '【マグマフォージ】')
        ).length;

        if (magmaForgeCount > 0) {
          const attackBonus = magmaForgeCount * 500;
          modifyAttack(context as any, attackBonus, monsterIndex, false, true);
        }
      },
    },
  ],

  /**
   * C0000171: 岩狸・熔岩権蔵・極
   * 【召喚時】自分の場にいる《岩狸》モンスター1体を破壊し、その攻撃力の半分のダメージを相手プレイヤーに与える。
   */
  C0000171: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 《岩狸》1体破壊、攻撃力半分を相手に',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog } = context;
        const { myField } = getPlayerContext(context);

        // 自分以外の《岩狸》を探す
        const tanukiIndex = myField.findIndex((monster, idx) =>
          monster &&
          idx !== monsterIndex &&
          monster.name &&
          monster.name.includes('岩狸')
        );

        if (tanukiIndex === -1) {
          addLog('岩狸・熔岩権蔵・極の効果: 破壊できる《岩狸》がいません', 'info');
          return;
        }

        const targetMonster = myField[tanukiIndex];
        if (!targetMonster) return;
        const damageAmount = Math.floor((targetMonster.attack ?? 0) / 2);

        // モンスターを破壊
        destroyMonster(context as any, tanukiIndex, false);

        // ダメージを与える
        conditionalDamage(context as any, damageAmount, 'opponent');
        addLog(`岩狸・熔岩権蔵・極の効果: ${targetMonster.name}を破壊、相手に${damageAmount}ダメージ`, 'damage');
      },
    },
  ],

  /**
   * C0000172: 岩狸・禁忌の熔岩帝
   * 【召喚時】場にいる全てのモンスターに3000ダメージを与え、相手プレイヤーに破壊したモンスターの数×400ダメージを与える。
   */
  C0000172: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 全モンスターに3000、破壊数×400を相手に',
      effect: (context: TriggerContext) => {
        const { setP1Field, setP2Field, addLog } = context;
        let destroyedCount = 0;

        // 両プレイヤーのフィールドをチェック
        [1, 2].forEach((player) => {
          const setField = player === 1 ? setP1Field : setP2Field;

          setField!((prev) => {
            return prev.map((monster) => {
              if (monster) {
                const newHp = monster.currentHp - 3000;
                if (newHp <= 0) {
                  destroyedCount++;
                  addLog(`${monster.name}が破壊された！`, 'damage');
                  return null;
                } else {
                  return { ...monster, currentHp: newHp };
                }
              }
              return monster;
            });
          });
        });

        // 破壊した数×400のダメージ
        if (destroyedCount > 0) {
          const totalDamage = destroyedCount * 400;
          addLog(`岩狸・禁忌の熔岩帝の効果: ${destroyedCount}体破壊、相手に${totalDamage}ダメージ`, 'damage');
          conditionalDamage(context as any, totalDamage, 'opponent');
        }
      },
    },
  ],

  /**
   * C0000312: フレア・ワイバーン
   * 【自攻撃時】このカードと相手プレイヤーに300ダメージを与える。
   */
  C0000312: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 自分と相手に300ダメージ',
      effect: (context: TriggerContext) => {
        context.addLog('フレア・ワイバーンの効果: 自分と相手に300ダメージ', 'damage');
        conditionalDamage(context as any, 300, 'opponent');
        conditionalDamage(context as any, 300, 'self');
      },
    },
  ],

  /**
   * C0000359: ヴォランティス・インフェルノ
   * 【召喚時】自分のフィールドのモンスター1体を破壊し、相手プレイヤーに1000ダメージを与える。
   */
  C0000359: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 自分のモンスター1体破壊、相手に1000ダメージ',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog } = context;
        const { myField } = getPlayerContext(context);

        // 自分以外のモンスターを探す
        const targetIndex = myField.findIndex((monster, idx) =>
          monster && idx !== monsterIndex
        );

        if (targetIndex === -1) {
          addLog('ヴォランティス・インフェルノの効果: 破壊できるモンスターがいません', 'info');
          return;
        }

        // モンスターを破壊
        destroyMonster(context as any, targetIndex, false);

        // ダメージを与える
        conditionalDamage(context as any, 1000, 'opponent');
        addLog('ヴォランティス・インフェルノの効果: 相手に1000ダメージ', 'damage');
      },
    },
  ],

  /**
   * C0000361: 虹羽密林のフェザートラップ
   * 【被攻撃時】対象の相手モンスターの攻撃力をターン終了時まで600下げる。
   */
  C0000361: [
    {
      type: TRIGGER_TYPES.ON_ATTACKED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '被攻撃時: 攻撃者の攻撃力-600',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('虹羽密林のフェザートラップの効果: 攻撃者の攻撃力-600（未実装）', 'info');
        // TODO: 攻撃者の情報が必要
      },
    },
  ],

  /**
   * C0000369: 炎翼の鳥民・イグニス
   * 【召喚時】自分の手札1枚を墓地に送り、相手プレイヤーに600ダメージを与える。
   */
  C0000369: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 手札1枚捨て、相手に600ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myHand, setMyHand, setMyGraveyard } = getPlayerContext(context);

        if (myHand.length === 0) {
          addLog('炎翼の鳥民・イグニスの効果: 手札がありません', 'info');
          return;
        }

        // 最初のカードを墓地に送る（UIで選択させるべきだが、簡略化）
        const discardedCard = myHand[0];
        setMyHand((prev) => prev.slice(1));
        setMyGraveyard((prev) => [...prev, discardedCard]);

        addLog(`${discardedCard.name}を墓地に送った`, 'info');
        conditionalDamage(context as any, 600, 'opponent');
        addLog('炎翼の鳥民・イグニスの効果: 相手に600ダメージ', 'damage');
      },
    },
  ],

  /**
   * C0000373: 虹羽密林の血針蟻・ザルヴァキス
   * 【召喚時】自分の墓地の《虹羽密林の血針蟻・ザルヴァキス》を1体を召喚（同名カードはターンに1度）。
   */
  C0000373: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地の同名カードを召喚',
      effect: (context: TriggerContext) => {
        const success = reviveFromGraveyard(context as any, (card) => {
          return card.id === 'C0000373'; // 同名カード
        }, false); // 弱体化なし

        if (!success) {
          context.addLog('虹羽密林の血針蟻・ザルヴァキスの効果: 墓地に同名カードがいません', 'info');
        }
      },
    },
  ],

  /**
   * C0000377: 虹羽密林の赤花蔓・カルティノス
   * 【召喚時】自分のデッキの上から2枚を墓地に送り、その中に《虹羽密林》モンスターが含まれていた場合、相手プレイヤーに300ダメージを与える。
   */
  C0000377: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 2枚ミル、《虹羽密林》含むなら相手に300',
      effect: (context: TriggerContext) => {
        const milledCards = millDeck(context, 2);

        const hasRainbowFeatherJungle = milledCards.some((card) =>
          card.name && card.name.includes('虹羽密林')
        );

        if (hasRainbowFeatherJungle) {
          context.addLog('虹羽密林の赤花蔓・カルティノスの効果: 相手に300ダメージ', 'damage');
          conditionalDamage(context as any, 300, 'opponent');
        } else {
          context.addLog('虹羽密林の赤花蔓・カルティノスの効果: 《虹羽密林》がいませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000398: 呪術狩りの呪焔術師ガルドリック
   * 【召喚時】自分の墓地のコスト3の魔法カード1枚を手札に戻す。
   */
  C0000398: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地のコスト3魔法カードをサルベージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyHand } = getPlayerContext(context);

        // 墓地からコスト3の魔法カードを探す
        const targetCard = myGraveyard.find((card) =>
          card.type === 'magic' && card.cost === 3
        );

        if (!targetCard) {
          addLog('呪術狩りの呪焔術師ガルドリックの効果: 墓地にコスト3の魔法カードがありません', 'info');
          return;
        }

        // 墓地から手札に戻す
        setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetCard.uniqueId));
        setMyHand((prev) => [...prev, targetCard]);
        addLog(`${targetCard.name}を手札に戻した`, 'info');
      },
    },
  ],

  /**
   * C0000406: 血炎の精霊ブラッドフェニクス
   * 【自破壊時】相手プレイヤーに800ダメージを与える。
   */
  C0000406: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 相手に800ダメージ',
      effect: (context: TriggerContext) => {
        context.addLog('血炎の精霊ブラッドフェニクスの効果: 相手に800ダメージ', 'damage');
        conditionalDamage(context as any, 800, 'opponent');
      },
    },
  ],

  /**
   * C0000410: 峠の暴君ヴェルミグノス
   * 【自分ターン開始フェイズ時】相手フィールド全体に300ダメージを与える。
   */
  C0000410: [
    {
      type: TRIGGER_TYPES.ON_TURN_START_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'ターン開始時: 相手フィールド全体に300',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setOpponentField } = getPlayerContext(context);

        addLog('峠の暴君ヴェルミグノスの効果: 相手フィールド全体に300ダメージ', 'damage');

        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              const newHp = monster.currentHp - 300;
              if (newHp <= 0) {
                addLog(`${monster.name}が破壊された！`, 'damage');
                return null;
              } else {
                return { ...monster, currentHp: newHp };
              }
            }
            return monster;
          });
        });
      },
    },
  ],

  /**
   * C0000411: 呪炎殻のイグニファトゥス
   * 【自攻撃時】自分の攻撃を200アップ。
   */
  C0000411: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 自分の攻撃力+200',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('呪炎殻のイグニファトゥスの効果: 攻撃力+200', 'info');
        // accumulatedDamageBonus を使用（React state更新の非同期問題を回避）
        context.accumulatedDamageBonus = (context.accumulatedDamageBonus || 0) + 200;
      },
    },
  ],

  /**
   * C0000412: 血針の蠍スコーピグニス
   * 【召喚時】自分のデッキの上から2枚を墓地に送る。
   */
  C0000412: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 2枚ミル',
      effect: (context: TriggerContext) => {
        context.addLog('血針の蠍スコーピグニスの効果: デッキから2枚を墓地に送る', 'info');
        millDeck(context, 2);
      },
    },
  ],

  // ========================================
  // フィールドカード
  // ========================================

  /**
   * C0000037: ドラゴンの火山
   * 【常時】《ドラゴン》と名のついたモンスターの攻撃力を400アップ。→ continuousEffectsで実装
   * 【自分エンドフェイズ時】相手モンスターに300ダメージを与え、場に炎属性モンスターがいる場合、さらに相手プレイヤーに400ダメージを与える。
   */
  // ========================================
  // フェイズカード
  // ========================================

  /**
   * C0000185: 岩狸の山里
   * 1枚重ね: 【エンドフェイズ時】自分の《岩狸》モンスター1体のHPを400下げ、相手プレイヤーに400ダメージ。
   * 3枚重ね: 【自分エンドフェイズ時】場にいる《岩狸》モンスター1体につき相手プレイヤーに600ダメージを与え、その後このカードを墓地に送る。
   * ※常時効果（初期効果、2枚重ね）はcontinuousEffects/effectDefinitions/phaseCards.jsで実装済み
   */
  C0000185: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '1枚重ね時: 岩狸1体HP-400、相手に400ダメージ / 3枚重ね時: 岩狸1体につき600ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog, phaseCard } = context;
        const { myField, setMyField, setOpponentLife, myPhaseCard, setMyPhaseCard, setMyGraveyard } = getPlayerContext(context);

        // フェイズカードのチャージ数を確認
        const card = phaseCard || myPhaseCard;
        if (!card || card.id !== 'C0000185') return;

        const chargeCount = card.charges ? card.charges.length : 0;

        // 1枚重ね効果（chargeCount === 1）
        if (chargeCount === 1) {
          const tanukiMonsters = myField.filter(m => m && m.name && m.name.includes('岩狸'));
          if (tanukiMonsters.length === 0) {
            addLog('岩狸の山里: 岩狸モンスターがいないため効果発動なし', 'info');
            return;
          }

          // 最初の岩狸のHPを400下げる
          const targetIndex = myField.findIndex(m => m && m.name && m.name.includes('岩狸'));
          let destroyedMonster: MonsterInstance | null = null;

          setMyField(prev => {
            const newField = [...prev];
            if (newField[targetIndex]) {
              const newHp = newField[targetIndex]!.currentHp - 400;
              if (newHp <= 0) {
                addLog(`${newField[targetIndex]!.name}がHP減少で倒れた！`, 'damage');
                destroyedMonster = newField[targetIndex];
                newField[targetIndex] = null;
              } else {
                newField[targetIndex] = { ...newField[targetIndex]!, currentHp: newHp };
                addLog(`岩狸の山里: ${newField[targetIndex]!.name}のHP-400`, 'damage');
              }
            }
            return newField;
          });

          // 破壊されたモンスターを墓地へ
          if (destroyedMonster) {
            setMyGraveyard(prev => [...prev, destroyedMonster as MonsterInstance]);
          }

          // 相手に400ダメージ
          setOpponentLife(prev => prev - 400);
          addLog('岩狸の山里: 相手プレイヤーに400ダメージ！', 'damage');
        }

        // 3枚重ね効果（chargeCount === 3）
        if (chargeCount === 3) {
          const tanukiCount = myField.filter(m => m && m.name && m.name.includes('岩狸')).length;
          if (tanukiCount === 0) {
            addLog('岩狸の山里: 岩狸モンスターがいないため効果発動なし', 'info');
          } else {
            const totalDamage = tanukiCount * 600;
            setOpponentLife(prev => prev - totalDamage);
            addLog(`岩狸の山里: 岩狸${tanukiCount}体で相手に${totalDamage}ダメージ！`, 'damage');
          }

          // このカードを墓地に送る
          if (setMyPhaseCard) {
            setMyPhaseCard(null);
            setMyGraveyard(prev => [...prev, card]);
            addLog('岩狸の山里: 最終効果発動後、墓地へ送られた', 'info');
          }
        }
      },
    },
  ],

  /**
   * C0000038: 紅の竜宮
   * 【［ドラゴン］モンスターが破壊された時】手札からそのコスト以下の別の［ドラゴン］モンスター1体を効果無効で場に戻す（1ターンに1度）。
   */
  C0000038: [
    {
      type: TRIGGER_TYPES.ON_CATEGORY_MONSTER_DESTROYED,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: 'ドラゴン破壊時: 手札からコスト以下のドラゴンを召喚（1ターン1度）',
      usedThisTurn: false,
      effect: (context: TriggerContext, trigger: any) => {
        const { destroyedCard, addLog, currentPlayer, summoningSickness = true } = context;
        const { myHand, setMyHand, myField, setMyField } = getPlayerContext(context);

        // 1ターン1度のチェック
        if (trigger && trigger.usedThisTurn) {
          addLog('紅の竜宮: このターン既に効果を使用しています', 'info');
          return;
        }

        // 破壊されたカードが［ドラゴン］かチェック
        if (!destroyedCard || !hasCategory(destroyedCard, '【ドラゴン】')) {
          return;
        }

        const destroyedCost = destroyedCard.cost || 0;

        // 手札から対象のドラゴンを探す（破壊されたカードのコスト以下）
        const eligibleDragons = myHand.filter(card =>
          card.type === 'monster' &&
          hasCategory(card, '【ドラゴン】') &&
          card.cost <= destroyedCost
        );

        if (eligibleDragons.length === 0) {
          addLog('紅の竜宮: 手札にコスト条件を満たすドラゴンがいません', 'info');
          return;
        }

        // 空きスロットを確認
        const emptySlotIndex = myField.findIndex(slot => slot === null);
        if (emptySlotIndex === -1) {
          addLog('紅の竜宮: フィールドに空きがありません', 'info');
          return;
        }

        // 最初のドラゴンを召喚（UIで選択させるべきだが、簡略化）
        const targetCard = eligibleDragons[0];

        // 手札から除去
        setMyHand(prev => prev.filter(c => c.uniqueId !== targetCard.uniqueId));

        // モンスターインスタンスを作成（効果無効）
        const monsterInstance = {
          ...targetCard,
          uniqueId: `${targetCard.id}_${Date.now()}_${Math.random()}`,
          currentHp: targetCard.hp,
          currentAttack: targetCard.attack,
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          usedSkillThisTurn: false,
          owner: currentPlayer,
          charges: [],
          statusEffects: [],
        };
        // SILENCE状態異常を付与（永続）
        statusEffectEngine.applyStatus(monsterInstance as MonsterInstance, STATUS_EFFECT_TYPES.SILENCE, {
          duration: -1, // 永続
          source: 'C0000038',
          sourceName: '紅の竜宮',
        });

        // フィールドに召喚
        setMyField(prev => {
          const newField = [...prev];
          newField[emptySlotIndex] = monsterInstance as MonsterInstance;
          return newField;
        });

        // 1ターン1度フラグを設定
        if (trigger) {
          trigger.usedThisTurn = true;
        }

        addLog(`紅の竜宮: 手札から「${targetCard.name}」を効果無効で召喚！`, 'info');
      },
      condition: (context: TriggerContext) => {
        // 破壊されたカードがドラゴンかチェック
        return context.destroyedCard && hasCategory(context.destroyedCard, '【ドラゴン】');
      },
    },
  ],

  C0000037: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: 相手モンスターに300ダメージ、炎モンスターがいれば相手に400ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, opponentField, setOpponentField, setOpponentLife, setOpponentGraveyard } = getPlayerContext(context);

        // 相手モンスター全体に300ダメージ
        const destroyedMonsters: MonsterInstance[] = [];
        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              const newHp = monster.currentHp - 300;
              if (newHp <= 0) {
                addLog(`${monster.name}がドラゴンの火山の炎で破壊された！`, 'damage');
                destroyedMonsters.push(monster);
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // 破壊されたモンスターを墓地に送る
        if (destroyedMonsters.length > 0) {
          setOpponentGraveyard((prev) => [...prev, ...destroyedMonsters]);
        }

        const hasOpponentMonsters = opponentField.some((m) => m !== null);
        if (hasOpponentMonsters) {
          addLog('ドラゴンの火山: 相手モンスター全体に300ダメージ！', 'damage');
        }

        // 場に炎属性モンスターがいるかチェック
        const hasFireMonster = myField.some((m) => m && m.attribute === '炎');
        if (hasFireMonster) {
          setOpponentLife((prev) => prev - 400);
          addLog('ドラゴンの火山: 炎モンスターの力で相手に400ダメージ！', 'damage');
        }
      },
    },
  ],

  /**
   * C0000188: 炎の岩峰（フィールドカード）
   * 【常時】炎属性モンスターの攻撃力を400アップ。（fieldCards.jsで実装済み）
   * 【エンドフェイズ時】場に3体以上炎属性モンスターがいる場合、相手に500ダメージ。
   */
  C0000188: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: 炎属性3体以上で相手に500ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, setOpponentLife } = getPlayerContext(context);

        // 場の炎属性モンスターをカウント
        const fireMonsterCount = myField.filter(m => m && m.attribute === '炎').length;

        if (fireMonsterCount >= 3) {
          setOpponentLife(prev => Math.max(0, prev - 500));
          addLog(`炎の岩峰: 炎属性モンスター${fireMonsterCount}体！相手に500ダメージ！`, 'damage');
        }
      },
    },
  ],

  /**
   * C0000200: 禁忌の熔岩大地（フィールドカード）
   * 【エンドフェイズ時】場にいる炎属性モンスター1体につきそのモンスターの攻撃力を200ダウン。
   * 【エンドフェイズ時】自分のライフを200減らす。
   * 【自壊時】このカードが墓地に送られた時、すべての炎属性モンスターに800ダメージ。
   */
  C0000200: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: 炎属性モンスターの攻撃力-200、ライフ-200',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, opponentField, setMyField, setOpponentField, setMyLife } = getPlayerContext(context);

        // 自分の場の炎属性モンスターの攻撃力を200ダウン
        let selfFireCount = 0;
        setMyField(prev => {
          return prev.map(monster => {
            if (monster && monster.attribute === '炎') {
              selfFireCount++;
              const currentAtk = monster.currentAttack ?? monster.attack ?? 0;
              return {
                ...monster,
                currentAttack: Math.max(0, currentAtk - 200),
              };
            }
            return monster;
          });
        });

        // 相手の場の炎属性モンスターの攻撃力を200ダウン
        let opponentFireCount = 0;
        setOpponentField(prev => {
          return prev.map(monster => {
            if (monster && monster.attribute === '炎') {
              opponentFireCount++;
              const currentAtk = monster.currentAttack ?? monster.attack ?? 0;
              return {
                ...monster,
                currentAttack: Math.max(0, currentAtk - 200),
              };
            }
            return monster;
          });
        });

        const totalFireCount = selfFireCount + opponentFireCount;
        if (totalFireCount > 0) {
          addLog(`禁忌の熔岩大地: 炎属性モンスター${totalFireCount}体の攻撃力を200ダウン`, 'info');
        }

        // 自分のライフを200減らす
        setMyLife(prev => Math.max(0, prev - 200));
        addLog('禁忌の熔岩大地: ライフ-200', 'damage');
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: すべての炎属性モンスターに800ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, opponentField, setMyField, setOpponentField, setMyGraveyard, setOpponentGraveyard } = getPlayerContext(context);

        const destroyedMy: MonsterInstance[] = [];
        const destroyedOpp: MonsterInstance[] = [];

        // 自分の場の炎属性モンスターに800ダメージ
        setMyField(prev => {
          return prev.map(monster => {
            if (monster && monster.attribute === '炎') {
              const newHp = monster.currentHp - 800;
              if (newHp <= 0) {
                destroyedMy.push(monster);
                addLog(`禁忌の熔岩大地の崩壊: ${monster.name}に800ダメージで破壊！`, 'damage');
                return null;
              }
              addLog(`禁忌の熔岩大地の崩壊: ${monster.name}に800ダメージ！`, 'damage');
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // 相手の場の炎属性モンスターに800ダメージ
        setOpponentField(prev => {
          return prev.map(monster => {
            if (monster && monster.attribute === '炎') {
              const newHp = monster.currentHp - 800;
              if (newHp <= 0) {
                destroyedOpp.push(monster);
                addLog(`禁忌の熔岩大地の崩壊: ${monster.name}に800ダメージで破壊！`, 'damage');
                return null;
              }
              addLog(`禁忌の熔岩大地の崩壊: ${monster.name}に800ダメージ！`, 'damage');
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // 破壊されたモンスターを墓地に送る
        if (destroyedMy.length > 0) {
          setMyGraveyard(prev => [...prev, ...destroyedMy]);
        }
        if (destroyedOpp.length > 0) {
          setOpponentGraveyard(prev => [...prev, ...destroyedOpp]);
        }
      },
    },
  ],

  /**
   * C0000187: 狸の熔岩郷
   * 【自分エンドフェイズ時】墓地の《岩狸》モンスター1体を場に戻す（攻撃力半分、HP半分、効果無効）。
   * 【エンドフェイズ時】自分のライフを300回復。
   */
  C0000187: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: 墓地から岩狸を蘇生（攻撃力・HP半分、効果無効）& ライフ300回復',
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer, summoningSickness = true } = context;
        const { myField, setMyField, myGraveyard, setMyGraveyard, setMyLife } = getPlayerContext(context);

        // 墓地から岩狸モンスターを探す
        const tanukiIndex = myGraveyard.findIndex(card =>
          card.type === 'monster' && card.name && card.name.includes('岩狸')
        );

        if (tanukiIndex !== -1) {
          // 空きスロットを探す
          const emptySlotIndex = myField.findIndex(slot => slot === null);

          if (emptySlotIndex !== -1) {
            const targetCard = myGraveyard[tanukiIndex];

            // 蘇生モンスターを作成（攻撃力・HP半分、効果無効）
            const revivedMonster = {
              ...targetCard,
              uniqueId: `${targetCard.id}_revive_${Date.now()}_${Math.random()}`,
              attack: Math.floor((targetCard.attack ?? 0) / 2),
              currentAttack: Math.floor((targetCard.attack ?? 0) / 2),
              hp: Math.floor((targetCard.hp ?? 0) / 2),
              currentHp: Math.floor((targetCard.hp ?? 0) / 2),
              canAttack: !summoningSickness, // 召喚酔い設定に依存
              usedSkillThisTurn: false,
              owner: currentPlayer,
              charges: [],
              statusEffects: [],
            };
            // SILENCE状態異常を付与（永続）
            statusEffectEngine.applyStatus(revivedMonster as MonsterInstance, STATUS_EFFECT_TYPES.SILENCE, {
              duration: -1, // 永続
              source: 'C0000187',
              sourceName: '狸の熔岩郷',
            });

            // 墓地から除去
            setMyGraveyard(prev => {
              const newGraveyard = [...prev];
              newGraveyard.splice(tanukiIndex, 1);
              return newGraveyard;
            });

            // フィールドに蘇生
            setMyField(prev => {
              const newField = [...prev];
              newField[emptySlotIndex] = revivedMonster as MonsterInstance;
              return newField;
            });

            addLog(`狸の熔岩郷: 墓地から「${targetCard.name}」を蘇生！（攻撃力${revivedMonster.attack}、HP${revivedMonster.hp}、効果無効）`, 'info');
          } else {
            addLog('狸の熔岩郷: フィールドに空きがないため蘇生できません', 'info');
          }
        } else {
          addLog('狸の熔岩郷: 墓地に岩狸モンスターがいません', 'info');
        }

        // ライフ300回復
        setMyLife(prev => prev + 300);
        addLog('狸の熔岩郷: ライフ300回復！', 'heal');
      },
    },
  ],

  // ========================================
  // ガーディアンモンスター
  // ========================================

  /**
   * C0000435: アッシュ・ガーディアン
   * 【自壊時】相手プレイヤーに600ダメージを与える。
   */
  C0000435: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 相手プレイヤーに600ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('アッシュ・ガーディアンの効果: 相手プレイヤーに600ダメージ', 'damage');
        conditionalDamage(context as any, 600, 'opponent');
      },
    },
  ],

  /**
   * C0000443: 紅焔のホムラ
   * 【召喚時】手札を1枚捨てる。捨てたカードのコスト×400ダメージを相手モンスター1体に与える。
   */
  C0000443: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 手札を1枚捨て、そのコスト×400ダメージを相手モンスター1体に',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingHandSelection, setPendingMonsterTarget } = context;
        const { myHand, setMyHand, setMyGraveyard, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        if (myHand.length === 0) {
          addLog('紅焔のホムラの効果: 手札がありません', 'info');
          return;
        }

        // 相手モンスターがいるか確認
        const opponentMonsters = opponentField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster }) => monster !== null);

        if (opponentMonsters.length === 0) {
          addLog('紅焔のホムラの効果: 相手フィールドにモンスターがいません', 'info');
          return;
        }

        // 手札選択処理
        const performDiscard = (discardCard: CardWithId) => {
          // 手札を捨てる
          setMyHand(prev => prev.filter(c => c.uniqueId !== discardCard.uniqueId));
          setMyGraveyard(prev => [...prev, discardCard]);

          const damage = (discardCard.cost || 0) * 400;
          addLog(`紅焔のホムラの効果: ${discardCard.name}(コスト${discardCard.cost})を捨てた！`, 'info');

          if (damage === 0) {
            addLog('紅焔のホムラの効果: コストが0のため、ダメージは与えられません', 'info');
            return;
          }

          // 相手モンスター選択後のダメージ処理
          const applyDamage = (targetIndex: number) => {
            const target = opponentField[targetIndex];
            if (!target) return;

            const newHp = target.currentHp - damage;
            addLog(`紅焔のホムラの効果: ${target.name}に${damage}ダメージ！`, 'damage');

            if (newHp <= 0) {
              addLog(`${target.name}は破壊された！`, 'damage');
              setOpponentGraveyard(prev => [...prev, target]);
              setOpponentField(prev => {
                const newField = [...prev];
                newField[targetIndex] = null;
                return newField;
              });
            } else {
              setOpponentField(prev => {
                const newField = [...prev];
                newField[targetIndex] = { ...target, currentHp: newHp };
                return newField;
              });
            }
          };

          // 相手モンスターが1体のみの場合は自動選択
          if (opponentMonsters.length === 1) {
            applyDamage(opponentMonsters[0].index);
            return;
          }

          // 複数いる場合は選択UI
          if (setPendingMonsterTarget) {
            setPendingMonsterTarget({
              message: `${damage}ダメージを与える相手モンスターを選択`,
              targetPlayer: 'opponent',
              callback: (selectedIndex: number) => {
                applyDamage(selectedIndex);
              },
              selectionOwner: context.currentPlayer,
            } as any);
          } else {
            // フォールバック
            applyDamage(opponentMonsters[0].index);
          }
        };

        // 手札が1枚のみの場合は自動選択
        if (myHand.length === 1) {
          performDiscard(myHand[0]);
          return;
        }

        // 複数枚の場合は選択UI
        if (setPendingHandSelection) {
          setPendingHandSelection({
            message: '捨てる手札を1枚選択してください（コスト×400ダメージを与えます）',
            count: 1,
            callback: (selectedCards: CardWithId[]) => {
              if (selectedCards && selectedCards.length > 0) {
                performDiscard(selectedCards[0]);
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          // フォールバック
          performDiscard(myHand[0]);
        }
      },
    },
  ],

  /**
   * C0000444: 蒸気の幻影ネーベル
   * 【召喚時】お互いのモンスター全体に400ダメージを与える。
   * 【自壊時】相手モンスター全体に400ダメージを与える。
   */
  C0000444: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: お互いのモンスター全体に400ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog, monsterIndex } = context;
        const { myField, setMyField, setMyGraveyard, opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        addLog('蒸気の幻影ネーベルの召喚時効果: お互いのモンスター全体に400ダメージ！', 'damage');

        // 自分のフィールドのモンスターに400ダメージ（自分自身は除外）
        const destroyedMy: MonsterInstance[] = [];
        setMyField(prev => {
          return prev.map((monster, idx) => {
            if (monster && idx !== monsterIndex) {
              const newHp = monster.currentHp - 400;
              if (newHp <= 0) {
                destroyedMy.push(monster);
                addLog(`${monster.name}が破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // 相手のフィールドのモンスターに400ダメージ
        const destroyedOpp: MonsterInstance[] = [];
        setOpponentField(prev => {
          return prev.map(monster => {
            if (monster) {
              const newHp = monster.currentHp - 400;
              if (newHp <= 0) {
                destroyedOpp.push(monster);
                addLog(`${monster.name}が破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // 破壊されたモンスターを墓地に送る
        if (destroyedMy.length > 0) {
          setMyGraveyard(prev => [...prev, ...destroyedMy]);
        }
        if (destroyedOpp.length > 0) {
          setOpponentGraveyard(prev => [...prev, ...destroyedOpp]);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: 相手モンスター全体に400ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        addLog('蒸気の幻影ネーベルの自壊時効果: 相手モンスター全体に400ダメージ！', 'damage');

        const destroyedOpp2: MonsterInstance[] = [];
        setOpponentField(prev => {
          return prev.map(monster => {
            if (monster) {
              const newHp = monster.currentHp - 400;
              if (newHp <= 0) {
                destroyedOpp2.push(monster);
                addLog(`${monster.name}が破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // 破壊されたモンスターを墓地に送る
        if (destroyedOpp2.length > 0) {
          setOpponentGraveyard(prev => [...prev, ...destroyedOpp2]);
        }
      },
    },
  ],
  // ========================================
  // 飛行モンスター
  // ========================================

  /**
   * C0000545: 炎翼竜
   * 【ダイレクトアタック成功時】相手の場の地上モンスター1体に500ダメージ。
   */
  C0000545: [
    {
      type: TRIGGER_TYPES.ON_DIRECT_ATTACK_SUCCESS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'ダイレクトアタック成功時: 地上モンスター1体に500ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog, slotIndex } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);
        const { isGroundMonster } = require('../keywordAbilities');

        // 相手フィールドの地上モンスターを探す
        const groundMonsters = opponentField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster }) => monster && isGroundMonster(monster));

        if (groundMonsters.length === 0) {
          addLog('炎翼竜の効果: 相手に地上モンスターがいません', 'info');
          return;
        }

        // ダメージを与える関数
        const dealDamageToGround = (targetIndex: number) => {
          const target = opponentField[targetIndex];
          if (!target) return;

          const newHp = target.currentHp - 500;
          addLog(`炎翼竜の効果: ${target.name}に500ダメージ！`, 'damage');

          if (newHp <= 0) {
            // 破壊
            setOpponentField(prev => {
              const newField = [...prev];
              newField[targetIndex] = null;
              return newField;
            });
            setOpponentGraveyard(prev => [...prev, target]);
            addLog(`${target.name}が破壊された！`, 'damage');
          } else {
            // HP減少
            setOpponentField(prev => {
              const newField = [...prev];
              if (newField[targetIndex]) {
                newField[targetIndex] = { ...newField[targetIndex], currentHp: newHp };
              }
              return newField;
            });
          }
        };

        if (groundMonsters.length === 1) {
          // 1体のみなら自動選択
          dealDamageToGround(groundMonsters[0].index);
        } else if (context.setPendingMonsterTarget) {
          // 複数いる場合は選択
          context.setPendingMonsterTarget({
            message: '500ダメージを与える地上モンスターを選択',
            targetPlayer: 'opponent',
            validTargets: groundMonsters.map(g => g.index),
            callback: dealDamageToGround,
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          // フォールバック: 最初のターゲットに適用
          dealDamageToGround(groundMonsters[0].index);
        }
      },
    },
  ],

  // ========================================
  // 彷徨い状態解除カード
  // ========================================

  /**
   * C0000573: 魂呼びの聖火巫女
   * 【召喚時】自分のモンスター全体の状態異常を全て解除する。
   */
  C0000573: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 味方モンスター全体の状態異常を全解除',
      effect: (context: TriggerContext) => {
        const { addLog, slotIndex } = context;
        const { myField, setMyField } = getPlayerContext(context);

        // 状態異常を持つ味方モンスターをカウント
        const monstersWithStatus = myField.filter((m, idx) => {
          if (!m || idx === slotIndex) return false;
          return m.statusEffects && m.statusEffects.length > 0;
        });

        if (monstersWithStatus.length === 0) {
          addLog('魂呼びの聖火巫女の効果: 状態異常を持つ味方モンスターがいません', 'info');
          return;
        }

        // 全味方モンスターの状態異常を解除
        let clearedCount = 0;
        setMyField((prev) => {
          return prev.map((monster, idx) => {
            if (!monster || idx === slotIndex) return monster;
            if (monster.statusEffects && monster.statusEffects.length > 0) {
              // 状態異常を解除
              statusEffectEngine.removeStatus(monster, null);
              // 攻撃力を元に戻す
              const originalAttack = monster.attack ?? monster.currentAttack ?? 0;
              clearedCount++;
              return {
                ...monster,
                currentAttack: originalAttack,
                statusEffects: [],
              };
            }
            return monster;
          });
        });

        addLog(`魂呼びの聖火巫女の効果: ${clearedCount}体の味方モンスターの状態異常を全て解除！`, 'heal');
      },
    },
  ],
};

/**
 * 炎属性カードがトリガー実装を持っているかチェック
 * @param {string} cardId - カードID
 * @returns {boolean} トリガー実装を持っている場合true
 */
export const hasFireCardTrigger = (cardId: string): boolean => {
  const triggers = fireCardTriggers as Record<string, unknown>;
  return cardId !== undefined && cardId !== null && triggers[cardId] !== undefined;
};

/**
 * 炎属性カードのトリガーを取得
 * @param {string} cardId - カードID
 * @returns {Array|null} トリガー配列、または null
 */
export const getFireCardTriggers = (cardId: string): TriggerDefinition[] | null => {
  const triggers = fireCardTriggers as unknown as Record<string, TriggerDefinition[]>;
  if (!cardId || !triggers[cardId]) {
    return null;
  }
  return triggers[cardId];
};

/**
 * 実装済み炎属性カード数を取得
 * @returns {number} 実装済みカード数
 */
export const getFireCardCount = () => {
  return Object.keys(fireCardTriggers).length;
};
